import { useState } from 'react'
import Cookies from 'js-cookie'
import { useTranslation } from 'react-i18next'

import { CookieTypes } from '@leanpay/models'
import { Button, Switch, Title } from '@leanpay/ui'

import { COOKIES_ARRAY, COOKIES_CONSENT } from './manage-cookies-consent'

interface EditCookiesDialogContentProps {
    onAcceptAllCookies: () => void
    onDeclineNonEssentialCookies: () => void
    onSaveCookies: (cookies: string[]) => void
}

const getCookieState = (cookieType: CookieTypes) => {
    const isCookieConsentEnabled = Cookies.get(COOKIES_CONSENT) === 'true'
    const allCookies = Cookies.get(COOKIES_ARRAY) ?? ''

    if (isCookieConsentEnabled && allCookies) {
        const cookies = allCookies.split(',')
        return cookies.includes(cookieType)
    }
    return cookieType === CookieTypes.Necessary
}

const EditCookiesDialogContent = ({
    onAcceptAllCookies,
    onDeclineNonEssentialCookies,
    onSaveCookies,
}: EditCookiesDialogContentProps) => {
    const { t } = useTranslation()

    const [necessaryCookies] = useState(() => getCookieState(CookieTypes.Necessary))
    const [analyticalCookies, setAnalyticalCookies] = useState(() =>
        getCookieState(CookieTypes.Analytical),
    )
    const [functionalCookies, setFunctionalCookies] = useState(() =>
        getCookieState(CookieTypes.Functional),
    )
    const [marketingCookies, setMarketingCookies] = useState(() =>
        getCookieState(CookieTypes.Marketing),
    )

    const handleSaveCookies = () => {
        const cookies = [CookieTypes.Necessary]
        if (analyticalCookies) cookies.push(CookieTypes.Analytical)
        if (functionalCookies) cookies.push(CookieTypes.Functional)
        if (marketingCookies) cookies.push(CookieTypes.Marketing)

        onSaveCookies(cookies)
    }

    return (
        <div className="flex flex-col">
            <Title className="">{t('cookieConsent.labels.manageCookie')}</Title>
            <Title className="text-[45px] mt-4 mb-3 leading-[45px]">🍪</Title>

            <div className="flex gap-2 mb-2">
                <Switch
                    checked={necessaryCookies}
                    disabled
                    id="necessaryCookies"
                    label={t('cookieConsent.necessaryCookies')}
                />
            </div>
            <h2 className="text-sm mt-3 mb-4 font-semibold">
                {t('cookieConsent.labels.optionalCookies')}
            </h2>
            <div className="flex gap-2 mb-4">
                <Switch
                    checked={analyticalCookies}
                    onCheckedChange={setAnalyticalCookies}
                    id="analyticalCookies"
                    label={t('cookieConsent.analyticalCookies')}
                />
            </div>
            <div className="flex gap-2 mb-2">
                <Switch
                    checked={functionalCookies}
                    onCheckedChange={setFunctionalCookies}
                    id="functionalCookies"
                    label={t('cookieConsent.functionalCookies')}
                />
            </div>
            <div className="flex gap-2">
                <Switch
                    checked={marketingCookies}
                    onCheckedChange={setMarketingCookies}
                    id="marketingCookies"
                    label={t('cookieConsent.marketingCookies')}
                />
            </div>

            <Button testId="accept-all-cookies" onClick={onAcceptAllCookies} className="mt-3">
                {t('cookieConsent.labels.acceptAll')}
            </Button>
            <Button
                testId="decline-non-essential-cookies"
                variant="secondary"
                onClick={onDeclineNonEssentialCookies}
                className="mt-2 min-h-[48px]"
            >
                {t('cookieConsent.labels.declineNonEssential')}
            </Button>
            <Button
                type="button"
                testId="save-cookies"
                variant="secondary"
                onClick={handleSaveCookies}
                className="mt-2 min-h-[48px]"
            >
                {t('cookieConsent.labels.save')}
            </Button>
        </div>
    )
}

export default EditCookiesDialogContent
